'use client';

import SaleCardVp from '@/components/common/cards/sale-card-vp/SaleCardVp';
import SaleCard from '@/components/common/cards/sale-card/SaleCard';
import ErrorMessage from '@/components/common/error-message/ErrorMessage';
import InsuranceWarningModal from '@/components/common/modals/insurance-warning-modal/InsuranceWarningModal';
import NoData from '@/components/common/no-data/NoData';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import useGetMyServices from '@/hooks/queries/services/useGetMyServices';
import useInsuranceDataComplete from '@/hooks/useInsuranceDataComplete';
import { myServicesData } from '@/services/api/services-api';
import { VEPCodes } from '@/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespaces } from '../../../../@types/i18next';
import SalePreviewCard from '@/components/common/cards/sale-preview-card/SalePreviewCard';

const ServiceGrid: React.FC<{ services: myServicesData[] }> = ({
  services
}) => {
  return (
    <div className="grid grid-cols-1 gap-6 my-10 md:grid-cols-2 lg:grid-cols-3 ">
      {services.map((service) => {
        const isVp = VEPCodes.includes(service.organization.code);
        // TODO: Change validation to check if the service has a division group
        const hasServiceDivisionGroups =
          service.saleItems[0].service.showOnlyInLanding;
        if (isVp) {
          return <SaleCardVp key={service.id} service={service} />;
        }
        if (hasServiceDivisionGroups) {
          return <SalePreviewCard key={service.id} service={service} />;
        }
        return <SaleCard key={service.id} service={service} />;
      })}
    </div>
  );
};

const CardSkeleton: React.FC = () => {
  return (
    <Card className="flex flex-col p-4 pt-6">
      <Skeleton className="w-full h-5" />
      <Skeleton className="w-full h-5 mt-8" />
      <Skeleton className="w-full h-5 mt-3" />
      <Skeleton className="w-full h-10 mt-6" />
    </Card>
  );
};

const BookingsSection = ({
  i18nNamespaces
}: {
  i18nNamespaces: (keyof I18nNamespaces)[];
}) => {
  const { t } = useTranslation(i18nNamespaces);

  const {
    data: services,
    isError,
    isLoading
  } = useGetMyServices<myServicesData[]>();

  //validation to check if user has completed all insurance data in all sales
  useInsuranceDataComplete();

  const { completedServices, activeServices, canceledServices } =
    useMemo(() => {
      if (!Array.isArray(services)) {
        return {
          completedServices: [],
          activeServices: [],
          canceledServices: []
        };
      }
      const completedServices = services.filter(
        (serv) => serv.service.completedPct === 100
      );
      const activeServices = services.filter(
        (serv) =>
          serv.service.completedPct !== 100 && serv.status.name !== 'canceled'
      );
      const canceledServices = services.filter(
        (serv) => serv.status.name === 'canceled'
      );

      return { completedServices, activeServices, canceledServices };
    }, [services]);

  if (isError) {
    return <ErrorMessage />;
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 my-10 md:grid-cols-2 lg:grid-cols-3 ">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }
  if (!Array.isArray(services)) {
    return <NoData />;
  }

  return (
    <div>
      <InsuranceWarningModal />
      {!!activeServices.length && <ServiceGrid services={activeServices} />}

      {!!completedServices.length && (
        <div>
          <p className="font-bold">{t('completedServices')}</p>
          <ServiceGrid services={completedServices} />
        </div>
      )}
      {!!canceledServices.length && (
        <div>
          <p className="font-bold">{t('canceledServices')}</p>
          <ServiceGrid services={canceledServices} />
        </div>
      )}
    </div>
  );
};

export default BookingsSection;
